import type { Account } from '../types/accounts.types'

/** The FGA domains that can grant access to an individual account. */
export const ACCOUNT_DOMAINS = [
  'accounts',
  'partner_accounts',
  'organisation_accounts',
] as const
export type AccountDomain = typeof ACCOUNT_DOMAINS[number]

/** The account fields that decide which domains govern it. */
export type AccountTenancy = Pick<
  Account,
  'partnerId' | 'organisationId' | 'admin'
>

/** An account in no tenant, e.g. one reached through the id-only account profile. */
export const TENANTLESS_ACCOUNT: AccountTenancy = {
  partnerId: null,
  organisationId: null,
  admin: null,
}

/**
 * The domains that can see an account, mirroring pbp-api's
 * `iam/domain/account/account-domains.ts` - keep the two in step. `accounts`
 * sees every account; a tenant domain sees its own tenant's accounts. An
 * account with no tenant at all belongs to `organisation_accounts`.
 */
export const domainsCoveringAccount = ({
  partnerId,
  organisationId,
  admin,
}: AccountTenancy): AccountDomain[] => {
  const tenantless = !partnerId && !organisationId && !admin

  return [
    'accounts',
    ...(partnerId ? (['partner_accounts'] as const) : []),
    ...(organisationId || tenantless
      ? (['organisation_accounts'] as const)
      : []),
  ]
}

/**
 * The domains that can modify an account. An admin account is only ever
 * managed through `accounts`, even one that is also in a tenant.
 */
export const domainsModifyingAccount = (
  account: AccountTenancy
): AccountDomain[] =>
  account.admin ? ['accounts'] : domainsCoveringAccount(account)

/** `domain:action` for each domain, for an `{ any }` permission check. */
export const accountDomainPermissions = (
  domains: readonly AccountDomain[],
  action: string
): string[] => domains.map((domain) => `${domain}:${action}`)
